import { faListCheck, faGear } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Typography from '@mui/material/Typography'
import {
  StatusChipV3,
  StatusChipBg,
  StatusChipText,
} from '@labrav/react-components'
import { FundingRun } from '../../api/fundingApi'
import { SectionCard } from './SectionCard'
import { DetailField } from './DetailField'
import { useStyles } from './ProgramsFundingDetails.styles'

interface ProgramsFundingDetailsProps {
  run: FundingRun
  /** Opens funding settings, where the submission type is configured. */
  onOpenSettings?: () => void
}

const toPercent = (value: number | null | undefined): string | null =>
  value == null ? null : `${Math.round(value * 100)}%`

export const ProgramsFundingDetails = ({
  run,
  onOpenSettings,
}: ProgramsFundingDetailsProps) => {
  const { classes } = useStyles()
  const fields = run.detail_fields ?? {}
  const insights = run.last_result_json ?? {}
  const confidence = toPercent(fields.confidence ?? insights.confidence)
  const acceptance = toPercent(
    fields.acceptance_chance ?? insights.acceptance_chance
  )

  const headerRight =
    confidence || acceptance ? (
      <div className={classes.metrics}>
        {confidence && (
          <Typography component="span" className={classes.metric}>
            <b>{confidence}</b> Confidence
          </Typography>
        )}
        {acceptance && (
          <Typography component="span" className={classes.metric}>
            <b>{acceptance}</b> Acceptance chance
          </Typography>
        )}
      </div>
    ) : undefined

  // Both submission types read as a chip (Figma 364-43791), with the gear
  // beside it leading to where auto-submit is turned on and off.
  const submissionLabel = run.auto_submitted
    ? 'Auto-submitted with AI'
    : fields.submission_trigger
  const submissionTrigger = submissionLabel ? (
    <>
      <StatusChipV3
        bgColor={StatusChipBg.GRAY}
        textColor={StatusChipText.GRAY}
        label={submissionLabel}
        sizeVarient="normal"
      />
      {onOpenSettings && (
        <button
          type="button"
          className={classes.settingsButton}
          aria-label="Funding settings"
          data-testid="submission-settings"
          onClick={onOpenSettings}
        >
          <FontAwesomeIcon icon={faGear} />
        </button>
      )}
    </>
  ) : undefined

  return (
    <SectionCard
      icon={faListCheck}
      title="Programs and funding details"
      headerRight={headerRight}
      collapsible
      testId="funding-detail-programs"
    >
      <div className={classes.grid}>
        <DetailField label="Program" value={run.program} />
        <DetailField label="Sub-category" value={fields.program_category} />
        <span className={classes.divider} />
        <DetailField label="Type of funding" value={fields.funding_type} />
        <DetailField
          label="Fund request ID"
          value={run.fund_request_id}
          copyValue={run.fund_request_id}
        />
        <span className={classes.divider} />
        <DetailField label="Submission type" value={submissionTrigger} />
        <DetailField label="Project name" value={fields.project_name} />
        <span className={classes.divider} />
        <DetailField label="Description" value={fields.description} fullWidth />
        <span className={classes.divider} />
        <DetailField
          label="AWS Opportunity ID"
          value={run.opportunity_id}
          copyValue={run.opportunity_id}
        />
        <DetailField
          label="CRM Opportunity ID"
          value={run.crm_opportunity_id}
          copyValue={run.crm_opportunity_id}
        />
      </div>
    </SectionCard>
  )
}
